import type { Polygon, Shape, World } from "./shapes";

// The width of the map against its height. The frame is built to it and global.css draws the box at it, so the
// picture fills the box exactly rather than being letterboxed into it or cropped by it.
export const ASPECT = 2;

// Every map is drawn into the same box and the frame is scaled onto it, rather than the frame being the box: a
// country is between a hundred metres and half a world across, and a viewBox in degrees would mean a stroke width
// and a rounding that mean something different on every one of them. Vatican City is where that showed — three
// decimals of a degree is a tenth of a millimetre on the map of Russia and the whole country here.
export const VIEW_BOX = "0 0 1000 500";

const WIDTH = 1000;

const HEIGHT = WIDTH / ASPECT;

export interface Land {
  code: string;
  path: string;
}

export interface CountryMap {
  // The country the map is of, absent where the reader's point of view draws no boundary for it at all.
  own: string | undefined;
  // Its land neighbours, each one a country this page can show and so each one the way to the next.
  borders: readonly Land[];
  // Whatever else the frame happens to hold, which is what keeps the map from being a country in an empty sea.
  rest: readonly Land[];
  // Where this one was drawn, which is the whole of what the next one needs to be flown from.
  framing: Framing;
  // Where the map before it was framed, in this map's own units, when that is somewhere worth flying from.
  from: Box | undefined;
}

// Everything about a map the next one has to know. A country is drawn about its own middle, so no two maps share a
// coordinate system and moving between them is a conversion rather than a subtraction.
export interface Framing {
  meridian: number;
  parallel: number;
  frame: Box;
}

// A polygon with the box it sits in, measured once when the boundaries arrive rather than on every country picked:
// what the frame costs is then a comparison per country and coordinates are only walked for the few in it.
export interface Part {
  polygon: Polygon;
  west: number;
  east: number;
  north: number;
  south: number;
}

export type Prepared = ReadonlyMap<string, readonly Part[]>;

// Where a country is and roughly how far across in degrees, for framing one that has no boundary in the data to be
// framed on: Natural Earth files Réunion inside France and Svalbard inside Norway, and a map over the right patch
// of sea still shows the island, drawn as part of whoever it was filed under.
export interface Place {
  longitude: number;
  latitude: number;
  across: number;
}

export function prepare(world: World): Prepared {
  return new Map(Object.entries(world).map(([code, shape]) => [code, boxesOf(shape)]));
}

// Measured once per shape rather than once per view: a point of view is a patch, so the twenty-odd countries it
// redraws are the only arrays that are not the very ones the default view holds, and picking a second view walks
// the coordinates of those alone.
const BOXED = new WeakMap<Shape, readonly Part[]>();

function boxesOf(shape: Shape): readonly Part[] {
  let parts = BOXED.get(shape);
  if (!parts) BOXED.set(shape, parts = shape.map(boxed));
  return parts;
}

// The map of one country: framed on where the default view puts it, and drawn from whichever view the reader has.
// Framing on the default is what gives a country its own map even where the reader's view has absorbed it into a
// neighbour — the frame is then over the land it would occupy, with whatever the view draws there filling it.
export function mapOf(
  shapes: Prepared,
  world: Prepared,
  code: string,
  borders: readonly string[],
  place: Place,
  from?: Framing,
): CountryMap | undefined {
  const pieces = world.get(code) ?? shapes.get(code);
  const middle = pieces && widest(pieces);
  const meridian = middle ? (middle.west + middle.east) / 2 : place.longitude;
  const parallel = middle ? (middle.north + middle.south) / 2 : place.latitude;
  const frame = fitted(pieces ? framed(pieces, meridian, parallel) : over(place, parallel));

  // An empty frame is a picture of nothing: Christmas Island and Tokelau are in no view at all and have no
  // neighbour to be drawn beside either, so the card says where they went instead of showing a rectangle of sea.
  // It is asked of the frame and never of what was drawn, since what a flight is drawn over reaches well past it.
  if (nothingIn(shapes, meridian, parallel, frame)) return undefined;

  // Where the reader was last looking, in this map's own units. A frame with nothing in common with this one is a
  // jump however it is drawn — half a world in between is a smear and not a journey — and one that has not moved is
  // the same country redrawn from another point of view, which is nothing to fly across either.
  const previous = from && reframed(from, meridian, parallel);
  const start = previous && overlaps(previous, frame) ? onto(previous, frame) : undefined;
  const flown = start && moved(start) ? start : undefined;
  // What is drawn reaches back over where the flight opens, or the first half of it is a picture of empty sea.
  const cover = previous && flown ? union(frame, previous) : frame;

  const drawn = (found: string): Land | undefined => {
    const path = pathOf(shapes.get(found), meridian, parallel, frame, cover);
    return path ? { code: found, path } : undefined;
  };

  const named = new Set([code, ...borders]);
  const own = pathOf(shapes.get(code), meridian, parallel, frame, cover) || undefined;
  const neighbours = borders.flatMap((border) => drawn(border) ?? []);
  const rest = [...shapes.keys()].filter((found) => !named.has(found)).flatMap((found) => drawn(found) ?? []);

  return { own, borders: neighbours, rest, framing: { meridian, parallel, frame }, from: flown };
}

// Whether the frame holds no land at all, which is the one thing that makes a map not worth drawing. It is asked of
// the shapes rather than of the paths because those are clipped to what a flight crosses rather than to the frame,
// and land found only back where the flight started is no part of this country's picture.
function nothingIn(shapes: Prepared, meridian: number, parallel: number, frame: Box): boolean {
  for (const parts of shapes.values()) {
    for (const part of parts) {
      if (overlaps(boxAt(part, meridian, parallel), frame)) return false;
    }
  }
  return true;
}

// The same rectangle of the world in this map's own projected units. Both are equirectangular and differ only in the
// meridian they are measured from and the parallel they are squeezed by, so one is an affine function of the other
// and a box stays a box: what changes across is a squeeze and a shift, and nothing at all changes down, a latitude
// being drawn the same way whichever country the map is of.
function reframed(from: Framing, meridian: number, parallel: number): Box {
  const squeeze = Math.cos(parallel * Math.PI / 180) / Math.cos(from.parallel * Math.PI / 180);
  const shift = x(from.meridian, meridian, parallel);
  return {
    left: from.frame.left * squeeze + shift,
    right: from.frame.right * squeeze + shift,
    top: from.frame.top,
    bottom: from.frame.bottom,
  };
}

// A box in projected units read as a rectangle of the drawn picture, which is the only place a flight is measured:
// the frame is the whole box by definition, so everything else is somewhere against it.
function onto(box: Box, frame: Box): Box {
  const scale = WIDTH / (frame.right - frame.left);
  return {
    left: (box.left - frame.left) * scale,
    top: (box.top - frame.top) * scale,
    right: (box.right - frame.left) * scale,
    bottom: (box.bottom - frame.top) * scale,
  };
}

// A frame that has not really moved is no flight at all: a point of view picked redraws the same country at the very
// same frame, and a picture animated from where it already is is a second of work nobody can see.
function moved(start: Box): boolean {
  return Math.abs(start.left) > STILL || Math.abs(start.top) > STILL
    || Math.abs(start.right - WIDTH) > STILL || Math.abs(start.bottom - HEIGHT) > STILL;
}

// One part in a thousand of the picture, which is a pixel of it at about the widest the box is ever drawn.
const STILL = 1;

// Where a flight opens and how it reaches the frame it is flying to: the picture starts over the rectangle the map
// before it was framed on and moves onto the whole box, which is what makes a click on the map a pan and a zoom
// rather than a cut. It is sampled rather than eased because the scale has to run geometrically — a zoom the eye
// reads as even is one that doubles in even steps, and there is no easing curve that says so — and the middle runs
// with the reciprocal of the scale, which is what holds the ground to one speed under the eye while the scale races.
// The stroke is divided by the scale as it goes, a border being a width on the picture and not one on the world:
// left alone, a map opening forty times zoomed in opens as a rectangle in the colour of the sea.
export function flight(from: Box, stroke: number): Keyframe[] {
  const opened = Math.max(WIDTH / (from.right - from.left), HEIGHT / (from.bottom - from.top));
  const across = (from.left + from.right) / 2;
  const down = (from.top + from.bottom) / 2;

  return Array.from({ length: STEPS + 1 }, (_, step) => {
    const through = step / STEPS;
    const scale = opened ** (1 - through);
    const along = opened === 1 ? through : (1 / opened - 1 / scale) / (1 / opened - 1);
    const middle = { x: across + (WIDTH / 2 - across) * along, y: down + (HEIGHT / 2 - down) * along };
    return {
      transform: `translate(${round(WIDTH / 2 - scale * middle.x)}px, ${round(HEIGHT / 2 - scale * middle.y)}px) `
        + `scale(${scale})`,
      strokeWidth: stroke / scale,
    };
  });
}

// How many places the flight is written out at. A fortyfold zoom is the widest any of these ever is, and twenty-four
// steps of it are a sixteenth apart, which is closer than the straight line between two of them can be seen to be.
const STEPS = 24;

// A box of the country's own size about the point given for it, which is the only thing left to frame on when there
// is no boundary at all.
function over(place: Place, parallel: number): Box {
  const half = place.across / 2;
  const across = half * Math.cos(parallel * Math.PI / 180);
  // The projection puts the meridian at nothing across, and the latitude at whatever y makes of it.
  return { left: -across, right: across, top: y(place.latitude) - half, bottom: y(place.latitude) + half };
}

// Equirectangular about the country's own middle: longitudes are measured from a meridian through it and squeezed by
// the cosine of a parallel through it, which is the whole of what keeps a country from being stretched sideways the
// further from the equator it sits. It is the projection an atlas gives a single country, and the only one worth the
// arithmetic here — nothing on this map is measured, compared by area, or read at more than one scale at a time.
function x(longitude: number, meridian: number, parallel: number): number {
  return (longitude - meridian) * Math.cos(parallel * Math.PI / 180);
}

// South is down, which is what the negation is: an SVG's y grows the way a latitude shrinks.
function y(latitude: number): number {
  return -latitude;
}

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// Where the map is framed. A country is not always one place — France reaches South America, Norway reaches
// Svalbard, the United States reaches Hawaii — and a frame around the whole of one is a frame around an ocean with
// something in each corner. So the frame is grown from the largest piece: first over every piece big enough to be
// worth the room and close enough to reach, and then over anything at all that is nearly touching it already, which
// is what keeps Tasmania on the map of Australia and French Guiana off the map of France.
function framed(parts: readonly Part[], meridian: number, parallel: number): Box {
  const boxes = parts.map((part) => boxAt(part, meridian, parallel));
  const sizes = boxes.map(area);
  const biggest = sizes.indexOf(Math.max(...sizes));
  const largest = sizes[biggest];
  const taken = boxes.map((_, at) => at === biggest);

  let frame = boxes[biggest];

  for (let growing = true; growing;) {
    growing = false;
    boxes.forEach((box, at) => {
      if (taken[at] || sizes[at] < SIGNIFICANT * largest || gap(frame, box) > REACH * diagonal(frame)) return;
      frame = union(frame, box);
      taken[at] = true;
      growing = true;
    });
  }

  boxes.forEach((box, at) => {
    if (!taken[at] && gap(frame, box) <= NEARBY * diagonal(frame)) frame = union(frame, box);
  });

  return frame;
}

// How much of the country's largest piece another has to be worth before the frame is opened up to hold it: Alaska
// is a fifth of the United States and belongs on its map, where Hawaii is a five-hundredth and does not.
const SIGNIFICANT = 0.05;

// And how far away it may be while it is, measured against the frame so far: French Guiana is a seventh of France
// and six thousand kilometres from it, which is a distance no amount of size should buy a place in the frame.
const REACH = 1;

// What counts as nearly touching, for a piece too small to have earned the room on its own.
const NEARBY = 0.1;

// The frame with room left around the country, opened out to the shape the box is drawn at. Which side is opened is
// whichever one is short, so the country stays where it was and the room goes to its neighbours.
function fitted(frame: Box): Box {
  const margin = MARGIN * Math.max(frame.right - frame.left, frame.bottom - frame.top) || SMALLEST;
  const grown = {
    left: frame.left - margin,
    top: frame.top - margin,
    right: frame.right + margin,
    bottom: frame.bottom + margin,
  };

  const width = grown.right - grown.left;
  const height = grown.bottom - grown.top;
  const short = width / height < ASPECT;
  const wanted = short ? height * ASPECT : width / ASPECT;
  const half = (wanted - (short ? width : height)) / 2;

  return short
    ? { ...grown, left: grown.left - half, right: grown.right + half }
    : { ...grown, top: grown.top - half, bottom: grown.bottom + half };
}

// How much of the frame is left around the country, as a share of the longer of its two sides.
const MARGIN = 0.08;

// The margin for a country with no size at all to take a share of, which nothing in the data is and nothing has to
// be for a frame of zero width to be a picture nobody can see.
const SMALLEST = 0.01;

// The path for one country inside the frame, its polygons taken in turn and everything outside dropped before a
// single coordinate is written. The rings of a polygon go into one path and are filled even-odd, which is what
// makes a hole a hole: Lesotho is cut out of South Africa and the Vatican out of Italy. What is kept is measured
// against the cover rather than the frame, that being everywhere the picture passes over on its way in.
function pathOf(
  parts: readonly Part[] | undefined,
  meridian: number,
  parallel: number,
  frame: Box,
  cover: Box,
): string {
  const scale = WIDTH / (frame.right - frame.left);
  let path = "";

  for (const part of parts ?? []) {
    const shift = around(part, meridian);
    if (!overlaps(boxAt(part, meridian, parallel, shift), cover)) continue;
    for (const ring of part.polygon) {
      for (let at = 0; at < ring.length; at += 2) {
        const across = (x(ring[at] + shift, meridian, parallel) - frame.left) * scale;
        const down = (y(ring[at + 1]) - frame.top) * scale;
        path += `${at === 0 ? "M" : "L"}${round(across)} ${round(down)}`;
      }
      path += "Z";
    }
  }
  return path;
}

// A tenth of a unit is a ten-thousandth of the map, which is a tenth of a pixel at the widest the box is ever drawn.
function round(value: number): number {
  return Math.round(value * 10) / 10;
}

// Natural Earth cuts a ring at the antimeridian rather than letting one cross it, so a country either side of it is
// two polygons whose longitudes are 360 apart — Russia's east, Fiji, Kiribati, New Zealand's Chathams. Each is put
// in whichever turn of the world the map is centred on, and the seam then falls half a world from the country.
function around(part: Part, meridian: number): number {
  return -360 * Math.round(((part.west + part.east) / 2 - meridian) / 360);
}

function boxed(polygon: Polygon): Part {
  const ring = polygon[0];
  let west = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  let south = Infinity;

  for (let at = 0; at < ring.length; at += 2) {
    west = Math.min(west, ring[at]);
    east = Math.max(east, ring[at]);
    south = Math.min(south, ring[at + 1]);
    north = Math.max(north, ring[at + 1]);
  }

  return { polygon, west, east, north, south };
}

function boxAt(part: Part, meridian: number, parallel: number, shift = around(part, meridian)): Box {
  return {
    left: x(part.west + shift, meridian, parallel),
    right: x(part.east + shift, meridian, parallel),
    top: y(part.north),
    bottom: y(part.south),
  };
}

// The largest piece by the room it takes up, which is what the frame is grown from and what the projection is
// centred on: a country's own middle is where most of it is and not the middle of everything it owns.
function widest(parts: readonly Part[]): Part {
  return parts.reduce((found, part) => extent(part) > extent(found) ? part : found);
}

function extent(part: Part): number {
  return (part.east - part.west) * Math.cos((part.north + part.south) / 2 * Math.PI / 180) * (part.north - part.south);
}

function area(box: Box): number {
  return (box.right - box.left) * (box.bottom - box.top);
}

function diagonal(box: Box): number {
  return Math.hypot(box.right - box.left, box.bottom - box.top);
}

// How far apart two boxes are, which is nothing at all where they overlap on both axes.
function gap(a: Box, b: Box): number {
  return Math.hypot(
    Math.max(0, a.left - b.right, b.left - a.right),
    Math.max(0, a.top - b.bottom, b.top - a.bottom),
  );
}

function overlaps(a: Box, b: Box): boolean {
  return a.left <= b.right && b.left <= a.right && a.top <= b.bottom && b.top <= a.bottom;
}

function union(a: Box, b: Box): Box {
  return {
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(a.bottom, b.bottom),
  };
}
